"use client";

import React, { useState} from "react";
import Image from "next/image";
import {Box, Dialog, Stack, Typography, useTheme} from "@mui/material";
// eslint-disable-next-line import/no-cycle
import {
  BrochureSection as BrochureSectionType
} from "@/types/dto/landing-page/landing-page";
import HubspotEmbed from "@/components/forms/hubspot-embed";
import {useLocale, useTranslations} from "next-intl";
import HubspotFormDisclaimer from "@/components/forms/disclaimer/hubspot-form-disclaimer";
import DataLayer from "@/components/data-layer/data-layer";
import {DynamicCta} from "@/types/dto/common/dynamic-cta";
import DynamicCtaButton from "@/components/common/dynamic-cta-button/dynamic-cta-button";
import useViewBrochureOnline from "@/hooks/forms/use-view-brochure-online";
import RequestABrochureSection from "@/components/forms/request-a-brochure/request-a-brochure-section";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import useScrollToAnchor from "@/hooks/scroll-to-anchor/use-scroll-to-anchor";
import dynamicCtaButtonListFactory from "@/factories/common/dynamic-cta-button/dynamic-cta-button-factory";
import brochureDisplayFactory from "@/factories/brochure/brochure-factory";

export interface BrochureSectionProps extends BrochureSectionType {
  variant?: "embeddedForm" | "modalForm";
  moduleRankingPosition?: string;
}

const BrochureSection = ({
  basicInfo,
  brochureImage,
  anchorId,
  moduleRankingPosition = "5",
  variant = "modalForm"
}: BrochureSectionProps) => {
  const { headerText, copyText, ctas } = basicInfo;
  const t = useTranslations("BROCHURE");
  const locale = useLocale();

  const dynamicCtaList = ctas ? dynamicCtaButtonListFactory(ctas) : undefined;

  const viewBrochureOnlineForm = dynamicCtaList?.find(
    (value) => value.ctaType === "viewBrochureOnlineForm"
  ) as DynamicCta<"viewBrochureOnlineForm">;

  const requestABrochureForm = dynamicCtaList?.find(
    (value) => value.ctaType === "requestABrochureForm"
  ) as DynamicCta<"requestABrochureForm">;

  const brochureFormId = viewBrochureOnlineForm?.ctaData?.hubSpotForm?.formId
    ?? "";
  const targetId = `hubspotForm-${brochureFormId}-brochure-section-embedded-form`;

  const finalViewBrochureOnlineForm =  {
    callToActionUrl: viewBrochureOnlineForm?.ctaData?.url,
    callToActionText: viewBrochureOnlineForm?.label,
    hubspotFormInfo: viewBrochureOnlineForm?.ctaData.hubSpotForm
  };

  const {
    brochureCookieSet,
    viewOnlineSubmitted,
    viewOnlineDataLayerProps
  } = useViewBrochureOnline({
    viewBrochureOnlineForm: finalViewBrochureOnlineForm ?? {},
    targetId,
    instanceId: "embedded-brochure-section"
  });

  const [requestBrochureFormIsOpen, setRequestBrochureFormIsOpen] = useState<boolean>(false);
  const requestABrochureOnClick = () => {
    setRequestBrochureFormIsOpen((prev) => !prev);
  };

  const preSelectedBrochure = requestABrochureForm?.ctaData?.brochureDisplay?.preselectedBrochure ?? undefined;

  const formId = viewBrochureOnlineForm?.ctaData?.hubSpotForm?.formId;

  const showRAB = locale !== "en-nz" && (!!requestABrochureForm && !!requestABrochureForm?.ctaData?.brochureDisplay);

  const theme = useTheme();

  useScrollLock(requestBrochureFormIsOpen);
  useScrollToAnchor(anchorId);

  return (
    <Box
      component="section"
      className="fluid-box"
      id={anchorId}
      sx={{
        backgroundColor: "background.cool_strong",
        overflowX: "visible",
        position: "relative",
        "&::before": {
          content: "\"\"",
          backgroundImage: "url(\"https://i.gocollette.com/img/backgrounds/pattern.svg\")",
          opacity: 0.085,
          height: "22rem",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 0
        },
        "&::after": {
          content: "\"\"",
          position: "absolute",
          height: "22rem",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          background: `linear-gradient(to bottom, transparent 0%, transparent 50%, ${theme.palette.background.cool_strong} 100%)`
        }
      }}
      data-testid="brochure-section-container"
      data-track="visibility"
      data-parent-container-type="brochure form ctas"
      data-module-name="Brochure Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
    >
      <Stack
        className="destination-page-container"
        direction="row"
        sx={{
          gap: "2rem",
          overflowX: "visible",
          justifyContent: { xs: "center", md: "flex-start" },
          alignItems: "center",
          padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" },
          zIndex: 2,
          position: "relative"
        }}
      >
        <Stack
          sx={{
            gap: "2rem",
            maxWidth: "41rem",
            width: "100%",
            alignItems: { xs: "flex-start", sm: "center", md: "flex-start" },
            textAlign: { xs: "start", sm: "center", md: "start" }
          }}
        >
          <Typography variant="h2" data-testid="brochure-section-header">
            {headerText}
          </Typography>
          {(variant === "modalForm" || (variant === "embeddedForm" && !brochureCookieSet)) && (
            <Box
              sx={{
                fontSize: "1.8rem",
                fontWeight: 500,
                lineHeight: "3rem",
                "& p": { margin: 0 }
              }}
              dangerouslySetInnerHTML={{
                __html: copyText ?? ""
              }}
              data-testid="brochure-section-subtext"
            />
          )}
          {variant === "embeddedForm" && !brochureCookieSet && (
            <Box id="landing-page-view-brochure-embedded">
              <HubspotEmbed
                formId={formId ?? ""}
                dataLayerProps={null}
                targetId={targetId}
              />
            </Box>
          )}
          {variant === "embeddedForm" && viewOnlineSubmitted && (
            <DataLayer data={viewOnlineDataLayerProps ?? {}} />
          )}
          {variant === "embeddedForm" && brochureCookieSet && (
            <Box
              sx={{
                display: { xs: "block", md: "none" },
                width: "100%",
                maxWidth: "87rem",
                mx: "auto"
              }}
            >
              <Image
                src={brochureImage.imageUrl ?? ""}
                alt={brochureImage.altText ?? "Brochures"}
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block"
                }}
              />
            </Box>
          )}
          {(variant === "modalForm" || (variant === "embeddedForm" && brochureCookieSet))
            && viewBrochureOnlineForm
            && (
            <DynamicCtaButton
              label={viewBrochureOnlineForm.label ?? ""}
              ctaType={viewBrochureOnlineForm.ctaType}
              ctaData={viewBrochureOnlineForm.ctaData}
              testId="view-brochure-online-button"
              variant={viewBrochureOnlineForm.variant ?? "contained"}
              color={viewBrochureOnlineForm.color ?? "primary"}
              targetIdSuffix="brochure-section"
            />
          )}
          {showRAB && variant === "modalForm" && requestABrochureForm && (
            <DynamicCtaButton
              label={requestABrochureForm.label ?? ""}
              ctaType={requestABrochureForm.ctaType}
              ctaData={requestABrochureForm.ctaData}
              testId="mailing-brochure-button"
              variant={requestABrochureForm.variant ?? "contained"}
              color={requestABrochureForm.color ?? "primary"}
            />
          )}
          {showRAB && variant === "embeddedForm" && (
            <Typography
              data-testid="mailing-brochure-text-button"
              onClick={requestABrochureOnClick}
              sx={{
                fontSize: "1.8rem",
                textDecoration: "underline",
                "&:hover": {
                  cursor: "pointer"
                }
              }}
            >
              {t("Click here to receive a brochure by mail")}
            </Typography>
          )}
          {!brochureCookieSet && variant === "embeddedForm" && (
            <HubspotFormDisclaimer
              testId="landing-page-brochure-section"
              sx={{
                textAlign: {
                  xs: "start",
                  sm: "center",
                  md: "start"
                },
                padding: 0
            }}
            />
          )}
        </Stack>
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: "100%",
            maxWidth: "87rem",
            mx: "auto"
          }}
        >
          <Image
            src={brochureImage.imageUrl ?? ""}
            alt={brochureImage.altText ?? "Brochures"}
            width={0}
            height={0}
            sizes="100vw"
            style={{
              width: "100%",
              height: "auto",
              display: "block"
            }}
            data-testid="mailing-brochure-image"
          />
        </Box>
      </Stack>
      {showRAB && (
        <Dialog
          open={requestBrochureFormIsOpen}
          onClose={requestABrochureOnClick}
          disableScrollLock
          maxWidth="md"
          data-testid="request-a-brochure-modal"
          keepMounted
          fullWidth
        >
          <RequestABrochureSection
            handleClose={requestABrochureOnClick}
            brochureDisplay={requestABrochureForm?.ctaData?.brochureDisplay ?? brochureDisplayFactory()}
            isLandingPage
            preSelectedBrochure={requestABrochureForm?.ctaData?.brochureDisplay.preselectedBrochure ?? preSelectedBrochure}
          />
        </Dialog>
      )}
    </Box>
  );
};

export default BrochureSection;
